import { lazy, Suspense, useEffect, useState } from 'react'
import { api, haalSessie, opBevestigen, opUitgelogd } from './lib/api'
import { navigeer, useRoute } from './lib/router'
import { meldFout, zet } from './lib/staat'
import Poort from './onderdelen/Poort'
import Shell from './onderdelen/Shell'
import { Laden, Leeg } from './onderdelen/ui'
import Start from './onderdelen/Start'
import Tickets from './onderdelen/tickets/Tickets'

/* De schermen pas laden als iemand ze opent. Een medewerker die alleen een
   probleem meldt, haalt de kluis en de inventaris nooit binnen. De lijst
   met tickets niet: die staat ook op het overzicht. */
const TicketScherm = lazy(() => import('./onderdelen/tickets/TicketScherm'))
const Kluis = lazy(() => import('./onderdelen/kluis/Kluis'))
const GeheimScherm = lazy(() => import('./onderdelen/kluis/GeheimScherm'))
const Inventaris = lazy(() => import('./onderdelen/inventaris/Inventaris'))
const ItemScherm = lazy(() => import('./onderdelen/inventaris/ItemScherm'))
const Toegang = lazy(() => import('./onderdelen/toegang/Toegang'))
const SysteemScherm = lazy(() => import('./onderdelen/toegang/SysteemScherm'))
const Links = lazy(() => import('./onderdelen/Links'))
const Mensen = lazy(() => import('./onderdelen/mensen/Mensen'))
const PersoonScherm = lazy(() => import('./onderdelen/mensen/PersoonScherm'))
const Logboek = lazy(() => import('./onderdelen/Logboek'))
const QrScherm = lazy(() => import('./onderdelen/QrScherm'))
const Stickers = lazy(() => import('./onderdelen/inventaris/Stickers'))
const Overdracht = lazy(() => import('./onderdelen/Overdracht'))
const Instellingen = lazy(() => import('./onderdelen/Instellingen'))
const KijkSessies = lazy(() => import('./onderdelen/meekijken/KijkSessies'))
const MeekijkScherm = lazy(() => import('./onderdelen/meekijken/MeekijkScherm'))

type Stand = 'kijken' | 'binnen' | 'buiten'

export default function App() {
  const [stand, setStand] = useState<Stand>('kijken')

  /* Een fout uit de adresbalk (?fout=geen-toegang) wordt één keer getoond en
     dan weggehaald, zodat hij er na verversen niet nog steeds staat. */
  const [fout] = useState(() => new URLSearchParams(location.search).get('fout'))
  useEffect(() => {
    if (!fout) return
    const u = new URL(location.href)
    u.searchParams.delete('fout')
    history.replaceState(null, '', u.pathname + u.search)
  }, [fout])

  useEffect(() => {
    opUitgelogd(() => { zet({ ik: null }); setStand('buiten') })
    opBevestigen((waarom) => zet({ bevestigen: true, bevestigenWaarom: waarom ?? null }))
    void (async () => {
      const s = await haalSessie()
      if (!s) { setStand('buiten'); return }
      zet({ ik: s.ik })
      try {
        const w = await api.ik()
        zet({
          beheerder: w.beheerder, instellingen: w.instellingen === true, kluis: w.kluis, vestigingen: w.vestigingen,
          /* Zegt de functie het niet (nog niet), dan de rol uit de sessie. Het
             zet alleen knoppen aan; de functie it kijkt bij elke actie zelf. */
          ontwikkelaar: w.ontwikkelaar ?? s.ik.rollen.includes('developer'),
        })
      } catch (e) {
        /* Een 403 hier: het recht is ingetrokken sinds de inlog. */
        meldFout(e)
      }
      setStand('binnen')
    })()
  }, [])

  if (stand === 'kijken') return <div className="opstart"><Laden /></div>
  if (stand === 'buiten') return <Poort fout={fout} />

  return (
    <Shell>
      <Suspense fallback={<Laden />}>
        <Scherm />
      </Suspense>
    </Shell>
  )
}

function Scherm() {
  const { delen } = useRoute()
  const [eerste, tweede] = delen

  switch (eerste) {
    case undefined: return <Start />
    case 'tickets': return <Tickets />
    case 't': return tweede ? <TicketScherm id={tweede} /> : <Tickets />
    case 'kluis': return tweede ? <GeheimScherm id={tweede} /> : <Kluis />
    case 'inventaris': return <Inventaris groep={tweede ?? 'apparaten'} />
    case 'i': return tweede ? <ItemScherm id={tweede} /> : <Inventaris groep="apparaten" />
    case 'toegang': return <Toegang />
    case 's': return tweede ? <SysteemScherm id={tweede} /> : <Toegang />
    case 'links': return <Links />
    case 'mensen': return <Mensen />
    case 'm': return tweede ? <PersoonScherm id={tweede} /> : <Mensen />
    case 'logboek': return <Logboek />
    case 'q': return tweede ? <QrScherm code={tweede} /> : <NietGevonden />
    case 'stickers': return <Stickers />
    case 'overdracht': return <Overdracht />
    case 'instellingen': return <Instellingen />
    case 'kijk-sessies': return <KijkSessies />
    case 'meekijken': return tweede ? <MeekijkScherm id={tweede} /> : <KijkSessies />
    default: return <NietGevonden />
  }
}

function NietGevonden() {
  return (
    <div className="pagina">
      <Leeg titel="Deze pagina bestaat niet" tekst="Misschien is de link oud, of verkeerd overgenomen.">
        <button type="button" className="knop k-gewoon" onClick={() => navigeer('/')}>Naar je overzicht</button>
      </Leeg>
    </div>
  )
}
